import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Select,
  InputNumber,
  Space,
  Alert,
  Typography,
  message,
  Row,
  Col,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getAllAccounts,
  createManualJournalEntry,
  updateManualJournalEntry,
  getAllPartners,
  getJournalEntryBatch,
} from "../../api/accounting";

const { Option } = Select;
const { Title } = Typography;

/**
 * Create Manual Journal Entry Modal
 *
 * This modal is for creating MANUAL journal entries only.
 * These are adjustments, corrections, or transactions not captured elsewhere.
 *
 * AUTOMATIC journal entries are created by the system when:
 * - Creating invoices/bills (post_to_ledger)
 * - Allocating payments (PaymentAllocation.save)
 * - Allocating credits (CreditAllocation.save)
 * - Creating/allocating overpayments
 *
 * Manual entries MUST balance (Total Debits = Total Credits)
 * Uses /manual-journal-entries/ endpoint
 */
const CreateJournalEntryModal = ({
  visible,
  onCancel,
  onSuccess,
  selectedBrand,
  selectedEntry = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [journalEntryLines, setJournalEntryLines] = useState([
    { id: 1, account: null, debit: 0, credit: 0, description: "" },
    { id: 2, account: null, debit: 0, credit: 0, description: "" },
  ]);
  const [allAccountsList, setAllAccountsList] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBalanced, setIsBalanced] = useState(false);

  // Fetch all accounts
  const fetchAllAccounts = useCallback(async () => {
    if (!selectedBrand) return;

    try {
      const response = await getAllAccounts({ brand: selectedBrand.id });
      setAllAccountsList(response || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, [selectedBrand]);

  // Fetch all partners
  const fetchAllPartners = useCallback(async () => {
    if (!selectedBrand) return;

    try {
      const response = await getAllPartners({ brand: selectedBrand.id });
      console.log("Partners response:", response);

      // Handle different response structures
      let partnersArray = [];
      if (Array.isArray(response)) {
        partnersArray = response;
      } else if (response?.data?.results) {
        partnersArray = response.data.results;
      } else if (response?.results) {
        partnersArray = response.results;
      } else if (response?.data && Array.isArray(response.data)) {
        partnersArray = response.data;
      }

      console.log("Parsed partners array:", partnersArray);
      setAllPartners(partnersArray);
    } catch (error) {
      console.error("Error fetching partners:", error);
      setAllPartners([]);
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (visible) {
      fetchAllAccounts();
      fetchAllPartners();

      if (selectedEntry) {
        // Fetch all entries in the same batch/group
        const loadBatchEntries = async () => {
          try {
            console.log("Loading batch entries for entry:", selectedEntry);
            const batchEntries = await getJournalEntryBatch(selectedEntry.id);
            console.log("Batch entries loaded:", batchEntries);

            // Set form values from the first entry (they all share the same date/description for a batch)
            const firstEntry = batchEntries[0] || selectedEntry;
            form.setFieldsValue({
              date: dayjs(firstEntry.date),
              description: firstEntry.description || "",
              partner: firstEntry.partner_id || null,
            });

            // Convert batch entries to journal lines format
            const lines = batchEntries.map((entry, index) => ({
              id: entry.id || Date.now() + index,
              account: entry.account,
              debit: parseFloat(entry.debit || 0),
              credit: parseFloat(entry.credit || 0),
              description: entry.description || "",
            }));

            // Ensure at least 2 lines for proper double-entry
            if (lines.length < 2) {
              lines.push({
                id: Date.now(),
                account: null,
                debit: 0,
                credit: 0,
                description: "",
              });
            }

            setJournalEntryLines(lines);
          } catch (error) {
            console.error("Error loading batch entries:", error);
            message.error("Failed to load journal entry details");

            // Fallback: show just the selected entry
            form.setFieldsValue({
              date: dayjs(selectedEntry.date),
              description: selectedEntry.description || "",
              partner: selectedEntry.partner_id || null,
            });

            setJournalEntryLines([
              {
                id: selectedEntry.id || 1,
                account: selectedEntry.account,
                debit: parseFloat(selectedEntry.debit || 0),
                credit: parseFloat(selectedEntry.credit || 0),
                description: selectedEntry.description || "",
              },
              {
                id: Date.now(),
                account: null,
                debit: 0,
                credit: 0,
                description: "",
              },
            ]);
          }
        };

        loadBatchEntries();
      } else {
        form.resetFields();
        setJournalEntryLines([
          { id: 1, account: null, debit: 0, credit: 0, description: "" },
          { id: 2, account: null, debit: 0, credit: 0, description: "" },
        ]);
      }
    }
  }, [
    visible,
    fetchAllAccounts,
    fetchAllPartners,
    form,
    isEditMode,
    selectedEntry,
  ]);

  // Line management functions
  const addJournalLine = () => {
    const newLine = {
      id: Date.now(),
      account: null,
      debit: 0,
      credit: 0,
      description: "",
    };
    setJournalEntryLines([...journalEntryLines, newLine]);
  };

  const removeJournalLine = (lineId) => {
    if (journalEntryLines.length <= 2) {
      message.warning("Journal entry must have at least 2 lines");
      return;
    }
    setJournalEntryLines(
      journalEntryLines.filter((line) => line.id !== lineId)
    );
  };

  const updateJournalLine = (lineId, field, value) => {
    console.log(`Updating line ${lineId}, field ${field}, value:`, value);
    setJournalEntryLines((prevLines) =>
      prevLines.map((line) =>
        line.id === lineId ? { ...line, [field]: value } : line
      )
    );
  };

  // Check if entry is balanced
  useEffect(() => {
    console.log("Current journal entry lines:", journalEntryLines);
    const totalDebit = journalEntryLines.reduce(
      (sum, line) => sum + parseFloat(line.debit || 0),
      0
    );
    const totalCredit = journalEntryLines.reduce(
      (sum, line) => sum + parseFloat(line.credit || 0),
      0
    );
    console.log("Total Debit:", totalDebit, "Total Credit:", totalCredit);
    setIsBalanced(Math.abs(totalDebit - totalCredit) < 0.01);
  }, [journalEntryLines]);

  // Calculate totals
  const calculateTotals = useMemo(() => {
    const totalDebit = journalEntryLines.reduce(
      (sum, line) => sum + parseFloat(line.debit || 0),
      0
    );
    const totalCredit = journalEntryLines.reduce(
      (sum, line) => sum + parseFloat(line.credit || 0),
      0
    );
    return { totalDebit, totalCredit, difference: totalDebit - totalCredit };
  }, [journalEntryLines]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate that debits equal credits
      if (Math.abs(calculateTotals.difference) > 0.01) {
        message.error("Debits must equal Credits. Please balance the entry.");
        return;
      }

      // Validate that all lines have accounts
      const invalidLines = journalEntryLines.filter((line) => !line.account);
      if (invalidLines.length > 0) {
        message.error("All lines must have an account selected");
        return;
      }

      setLoading(true);

      // Prepare the data for backend
      const journalData = {
        brand_id: selectedBrand.id,
        date: values.date.format("YYYY-MM-DD"),
        description: values.description || "",
        partner_id: values.partner || null,
        entries: journalEntryLines.map((line) => ({
          account_id: line.account,
          debit: parseFloat(line.debit || 0),
          credit: parseFloat(line.credit || 0),
          description: line.description || values.description || "",
        })),
      };

      if (isEditMode && selectedEntry) {
        await updateManualJournalEntry(selectedEntry.id, journalData);
        message.success("Journal entry updated successfully");
      } else {
        await createManualJournalEntry(journalData);
        message.success("Journal entry created successfully");
      }
      onSuccess();
    } catch (error) {
      if (error?.errorFields) {
        return; // Form validation error
      }
      console.error("Error creating journal entry:", error);
      message.error(
        error?.response?.data?.detail || "Failed to create journal entry"
      );
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Account",
      dataIndex: "account",
      width: 300,
      render: (value, record) => (
        <Select
          value={value}
          onChange={(val) => updateJournalLine(record.id, "account", val)}
          placeholder="Select account"
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => {
            const optionText = option?.children;
            if (!optionText || !input) return false;
            return (
              String(optionText).toLowerCase().indexOf(input.toLowerCase()) >= 0
            );
          }}
          disabled={!!selectedEntry && !isEditMode}
          virtual={true}
          listHeight={256}
        >
          {allAccountsList.map((account) => (
            <Option key={account.id} value={account.id}>
              {account.code} - {account.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 250,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) =>
            updateJournalLine(record.id, "description", e.target.value)
          }
          placeholder="Line description (optional)"
          disabled={!!selectedEntry && !isEditMode}
        />
      ),
    },
    {
      title: "Debit",
      dataIndex: "debit",
      width: 150,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => {
            console.log(
              "Debit onChange called with:",
              val,
              "for record:",
              record.id
            );
            const numVal = val === null || val === undefined ? 0 : val;
            updateJournalLine(record.id, "debit", numVal);
            // Auto-clear credit if debit is entered
            if (numVal > 0) {
              updateJournalLine(record.id, "credit", 0);
            }
          }}
          min={0}
          precision={2}
          step={0.01}
          style={{ width: "100%" }}
          prefix="$"
          placeholder="0.00"
          disabled={!!selectedEntry && !isEditMode}
          controls={true}
          keyboard={true}
        />
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      width: 150,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => {
            console.log(
              "Credit onChange called with:",
              val,
              "for record:",
              record.id
            );
            const numVal = val === null || val === undefined ? 0 : val;
            updateJournalLine(record.id, "credit", numVal);
            // Auto-clear debit if credit is entered
            if (numVal > 0) {
              updateJournalLine(record.id, "debit", 0);
            }
          }}
          min={0}
          precision={2}
          step={0.01}
          style={{ width: "100%" }}
          prefix="$"
          placeholder="0.00"
          disabled={!!selectedEntry && !isEditMode}
          controls={true}
          keyboard={true}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeJournalLine(record.id)}
          disabled={
            journalEntryLines.length <= 2 || (!!selectedEntry && !isEditMode)
          }
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        isEditMode
          ? "Edit Journal Entry"
          : selectedEntry
          ? "View Journal Entry"
          : "Create Manual Journal Entry"
      }
      open={visible}
      onCancel={onCancel}
      onOk={isEditMode ? handleSubmit : undefined}
      okText={isEditMode ? "Update Entry" : undefined}
      width={1200}
      destroyOnClose
      confirmLoading={loading}
      zIndex={2000}
      footer={
        isEditMode
          ? undefined
          : selectedEntry
          ? [
              <Button key="close" onClick={onCancel}>
                Close
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onCancel}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!isBalanced}
              >
                Create Entry
              </Button>,
            ]
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="date"
              label="Entry Date"
              rules={[{ required: true, message: "Please select a date" }]}
              initialValue={dayjs()}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="partner" label="Partner (Optional)">
              <Select
                placeholder="Select Partner"
                style={{ width: "100%" }}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {Array.isArray(allPartners) &&
                  allPartners.map((partner) => (
                    <Option
                      key={partner.id}
                      value={partner.id}
                      label={partner.name}
                    >
                      {partner.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="description" label="General Description">
              <Input placeholder="Enter general description for this entry" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Journal Entry Lines Table */}
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Journal Entry Lines
          </Title>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addJournalLine}
            size="small"
          >
            Add Line
          </Button>
        </div>

        <Table
          dataSource={journalEntryLines}
          rowKey="id"
          pagination={false}
          size="small"
          bordered
          scroll={{ x: true }}
          columns={columns}
          footer={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: "bold",
                padding: "8px 0",
              }}
            >
              <div>Totals:</div>
              <div>
                <Space size="large">
                  <span style={{ color: "#52c41a" }}>
                    Debit: ${calculateTotals.totalDebit.toFixed(2)}
                  </span>
                  <span style={{ color: "#ff4d4f" }}>
                    Credit: ${calculateTotals.totalCredit.toFixed(2)}
                  </span>
                  <span
                    style={{
                      color:
                        Math.abs(calculateTotals.difference) < 0.01
                          ? "#52c41a"
                          : "#ff4d4f",
                    }}
                  >
                    Difference: ${calculateTotals.difference.toFixed(2)}
                  </span>
                </Space>
              </div>
            </div>
          )}
        />

        {/* Balance Validation Alert */}
        {Math.abs(calculateTotals.difference) > 0.01 && (
          <Alert
            message="Entry is not balanced"
            description={`Debits must equal Credits. Current difference: $${Math.abs(
              calculateTotals.difference
            ).toFixed(2)}`}
            type="error"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
        {Math.abs(calculateTotals.difference) < 0.01 &&
          calculateTotals.totalDebit > 0 && (
            <Alert
              message="Entry is balanced"
              description="Debits equal Credits. Ready to post."
              type="success"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
      </div>
    </Modal>
  );
};

export default CreateJournalEntryModal;
